import React from 'react';
import { Grid, Box, Typography, Tooltip, IconButton } from '@mui/material';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import DeleteForeverTwoToneIcon from '@mui/icons-material/DeleteForeverTwoTone';
import {
    handleToggleDeleteTrainingModel,
    handleToggleFailedTrainingModal,
    handleToggleTrainingModelDetailsDialog,
} from '../../store/mlcreator-slice/mlcreator';
import {
    AccessTime,
    HourglassEmpty,
    Error,
    Settings,
    Analytics,
    Tune,
} from '@mui/icons-material';
import ShareTwoToneIcon from '@mui/icons-material/ShareTwoTone';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import { styled, keyframes } from '@mui/system';

enum MLAITrainingStatusEnum {
    Waiting = 10,
    Training = 20,
    CalculatingFeatureSignificance = 30,
    Completed = 40,
    Failed = 50,
}

interface TrainingStatusActionsProps {
    params: any;
    dispatch: any;
    t: any;
    // New prop: callback to select a model and go to Step 2
    onSelectModel: (modelRow: any, type: "prediction" | "optimization") => void;
    onOpenShare: (modelRow: any) => void;
}

const statusColorMap: Record<MLAITrainingStatusEnum, string> = {
    [MLAITrainingStatusEnum.Waiting]: '#787878',
    [MLAITrainingStatusEnum.Training]: '#49c44d',
    [MLAITrainingStatusEnum.CalculatingFeatureSignificance]: '#49c44d',
    [MLAITrainingStatusEnum.Failed]: '#f44336',
    [MLAITrainingStatusEnum.Completed]: '',
};

const pulse = keyframes`
    0% { transform: scale(1); opacity: 0.5; }
    50% { transform: scale(1.1); opacity: 1; }
    100% { transform: scale(1); opacity: 0.5; }
`;

const spin = keyframes`
    100% { transform: rotate(360deg); }
`;

const WaitingIcon = styled(AccessTime)(({ theme }) => ({
    animation: `${pulse} 1s infinite`,
    color: statusColorMap[MLAITrainingStatusEnum.Waiting],
}));

const CalculatingFeatureSignificanceIcon = styled(Settings)(({ theme }) => ({
    animation: `${spin} 1.5s linear infinite`,
    color: statusColorMap[
        MLAITrainingStatusEnum.CalculatingFeatureSignificance
    ],
}));

const FailedIcon = styled(Error)(({ theme }) => ({
    color: statusColorMap[MLAITrainingStatusEnum.Failed],
}));

const TrainingIcon = styled(Settings)(({ theme }) => ({
    animation: `${spin} 1.5s linear infinite`,
    color: statusColorMap[MLAITrainingStatusEnum.Training],
}));

const getStatusIcon = (status: MLAITrainingStatusEnum) => {
    switch (status) {
        case MLAITrainingStatusEnum.Waiting:
            return <WaitingIcon />;
        case MLAITrainingStatusEnum.CalculatingFeatureSignificance:
            return <CalculatingFeatureSignificanceIcon />;
        case MLAITrainingStatusEnum.Failed:
            return <FailedIcon />;
        case MLAITrainingStatusEnum.Training:
            return <TrainingIcon />;
        default:
            return null;
    }
};

const TrainingStatusActions: React.FC<TrainingStatusActionsProps> = ({
    params,
    dispatch,
    t,
    onSelectModel,
    onOpenShare,
}) => {
    const renderStatus = (
        icon: React.ReactNode,
        text: string,
        onClick?: () => void
    ) => (
        <Grid container spacing={1} justifyContent="left" alignItems="center" wrap="wrap">
            <Grid item xs="auto">
                <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    onClick={onClick}
                    style={{ cursor: onClick ? 'pointer' : 'default' }}
                >
                    {icon}
                    <Typography variant="body2">{t(text)}</Typography>
                </Box>
            </Grid>
        </Grid>
    );

    const renderFailedActions = (text: string) => (
        <Grid container display="flex" justifyContent="left">
            <Grid item >
                <Tooltip title={t('FAILED_ICON_TOOLTIP')}>
                    <IconButton
                        onClick={() =>
                            dispatch(
                                handleToggleFailedTrainingModal({
                                    isOpen: true,
                                    body: {
                                        message: t(params.row.trainingResponseError),
                                        description: params.row.trainingResponseErrorDetails,
                                    },
                                })
                            )
                        }
                    >
                        <FailedIcon />
                    </IconButton>
                </Tooltip>
            </Grid>
            <Grid item >
                <Tooltip title={t('CREATOR_INNER_DELETE_ACTION')}>
                    <IconButton
                        onClick={() =>
                            dispatch(
                                handleToggleDeleteTrainingModel({
                                    isOpen: true,
                                    body: { name: params.row.name, id: params.row.id },
                                })
                            )
                        }
                    >
                        <DeleteForeverTwoToneIcon className="delete-icon" />
                    </IconButton>
                </Tooltip>
            </Grid>
        </Grid>
    );

    const renderCompletedActions = () => (
        <Grid container display="flex" justifyContent="left">
            <Grid item >
                <Tooltip title={t('CREATOR_INNER_DETAILS_ACTION')}>
                    <IconButton
                        onClick={() =>
                            dispatch(
                                handleToggleTrainingModelDetailsDialog({
                                    isOpen: true,
                                    body: {
                                        id: params.row.id,
                                        name: params.row.name,
                                    },
                                })
                            )
                        }
                    >
                        <InfoTwoToneIcon className="info-icon" />
                    </IconButton>
                </Tooltip>
            </Grid>
            <Grid item >
                <Tooltip title={t('CREATOR_INNER_DELETE_ACTION')}>
                    <IconButton
                        onClick={() => {
                            dispatch(
                                handleToggleDeleteTrainingModel({
                                    isOpen: true,
                                    body: { name: params.row.name, id: params.row.id },
                                })
                            );
                        }}
                    >
                        <DeleteForeverTwoToneIcon className="delete-icon" />
                    </IconButton>
                </Tooltip>
            </Grid>
            <Grid item >
                <Tooltip title={t('CREATOR_INNER_SHARE_ACTION_TOOLTIP', { count: params.row.numberOfSharedModels || 0 })}>
                    <IconButton
                        onClick={() => {
                            onOpenShare(params.row);
                        }}
                    >
                        {params.row.numberOfSharedModels > 0 ? (
                            <ShareTwoToneIcon color="primary" fontSize="small" />
                        ) : (
                            <ShareOutlinedIcon sx={{ color: '#666' }} fontSize="small" />
                        )}
                    </IconButton>
                </Tooltip>
            </Grid>
            <Grid item  style={{ visibility: params.row.canBePredicted ? 'visible' : 'hidden' }}>
                <Tooltip title={t('CREATOR_INNER_REDIRECT_TO_PREDICTIONS_ACTION')}>
                    <IconButton
                        onClick={() => {
                            // Instead of navigation by URL, call onSelectModel
                            onSelectModel(params.row, 'prediction');
                        }}
                    >
                        <Analytics fontSize="small" className="redirect-icon" />
                    </IconButton>
                </Tooltip>
            </Grid>
            <Grid item style={{ visibility: params.row.canBeOptimised ? 'visible' : 'hidden' }}>
                <Tooltip title={t('CREATOR_INNER_REDIRECT_TO_OPTIMIZATION_ACTION')}>
                    <IconButton
                        onClick={() => {
                            // Instead of navigation by URL, call onSelectModel
                            onSelectModel(params.row, 'optimization');
                        }}
                    >
                        <Tune fontSize="small" className="optimization-icon" />
                    </IconButton>
                </Tooltip>
            </Grid>
        </Grid>
    );

    switch (params.row.trainingStatus) {
        case MLAITrainingStatusEnum.Waiting:
        case MLAITrainingStatusEnum.CalculatingFeatureSignificance:
        case MLAITrainingStatusEnum.Training:
            return renderStatus(
                getStatusIcon(params.row.trainingStatus),
                params.row.trainingStatusText
            );

        case MLAITrainingStatusEnum.Failed:
            return renderFailedActions(params.row.trainingStatusText);

        case MLAITrainingStatusEnum.Completed:
            return renderCompletedActions();

        default:
            return null;
    }
};

export default TrainingStatusActions;
